import React from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "./ui/card"
import { Button } from "./ui/button"
import { ArrowRight, Zap, LineChart, Code, Lock, Sparkles } from 'lucide-react'
import ProductDemo from './ProductDemo'

export default function LearnMoreSection() {
  return (
    <section className="relative overflow-hidden pt-8 pb-16 md:pt-12 md:pb-24">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-background to-background/80 dark:from-background dark:to-background/80 z-0"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
      
      <div className="container relative z-10 px-4 pt-8 pb-16 md:pt-12 md:pb-24 lg:pb-32 mx-auto max-w-6xl">
        {/* Interactive Product Demo */}
        <div className="max-w-5xl mx-auto mb-16 md:mb-20">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center px-4 py-1.5 mb-4 text-sm font-medium rounded-full bg-primary/10 text-primary dark:bg-primary/20">
              <Sparkles className="w-4 h-4 mr-2" />
              <span>See TenXer in Action</span>
            </div>
            
            <h4 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary via-secondary to-accent">
              Your Data, Just a Conversation Away
            </h4>
            
            <p className="text-foreground/80 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-8">
              Watch how TenXer translates natural language into powerful API calls, giving you instant access to your data without writing a single line of code.
            </p>
          </div>
          
          {/* Product Demo Component */}
          <ProductDemo />
          
          <div className="mt-8 text-center text-foreground/70 text-sm max-w-3xl mx-auto">
            <p>TenXer's AI understands your data sources and translates natural language into precise queries,
            delivering insights in seconds that would normally take hours of coding and analysis.</p>
          </div>
        </div>
        
        {/* Use Cases Section */}
        <div>
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Built For Your Entire Team</h3>
            <p className="text-foreground/70 max-w-2xl mx-auto">TenXer empowers everyone in your organization to work more efficiently with data.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Developer Card */}
            <Card className="overflow-hidden border border-border/50 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
              <div className="h-2 bg-gradient-to-r from-primary to-secondary"></div>
              <CardHeader>
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-primary/10 mr-3">
                    <Code className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle>For Software Developers</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-1 rounded-full bg-primary/10 mr-3 mt-1">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Natural Language API Interaction</p>
                    <p className="text-foreground/70 text-sm">Query and manipulate APIs using conversational commands instead of complex code.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-1 rounded-full bg-primary/10 mr-3 mt-1">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Universal Connectivity</p>
                    <p className="text-foreground/70 text-sm">Connect to any environment: localhost, staging, production, or APIs behind VPNs.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-1 rounded-full bg-primary/10 mr-3 mt-1">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Real-time Debugging</p>
                    <p className="text-foreground/70 text-sm">Inspect responses, identify issues, and test edge cases through conversation.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-1 rounded-full bg-primary/10 mr-3 mt-1">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Accelerated Development</p>
                    <p className="text-foreground/70 text-sm">Reduce boilerplate code and focus on building features that matter.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Product Manager Card */}
            <Card className="overflow-hidden border border-border/50 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
              <div className="h-2 bg-gradient-to-r from-secondary to-accent"></div>
              <CardHeader>
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-secondary/10 mr-3">
                    <LineChart className="h-5 w-5 text-secondary" />
                  </div>
                  <CardTitle>For Product & Business Teams</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-1 rounded-full bg-secondary/10 mr-3 mt-1">
                    <Zap className="h-4 w-4 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium">Self-service Data Analysis</p>
                    <p className="text-foreground/70 text-sm">Get answers to complex data questions without waiting for engineering or analytics teams.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-1 rounded-full bg-secondary/10 mr-3 mt-1">
                    <Zap className="h-4 w-4 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium">Trend Discovery</p>
                    <p className="text-foreground/70 text-sm">Uncover hidden patterns and emerging trends in customer behavior and product usage.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-1 rounded-full bg-secondary/10 mr-3 mt-1">
                    <Zap className="h-4 w-4 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium">Rapid Decision Making</p>
                    <p className="text-foreground/70 text-sm">Access key metrics in seconds to inform strategy and prioritization decisions.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-1 rounded-full bg-secondary/10 mr-3 mt-1">
                    <Zap className="h-4 w-4 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium">Faster Iteration Cycles</p>
                    <p className="text-foreground/70 text-sm">Test hypotheses and validate new features by querying real user data in real-time.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="relative mt-10 md:mt-10">
          <div className="absolute -inset-px bg-gradient-to-r from-primary/20 via-secondary/20 to-accent/20 rounded-xl blur-xl opacity-50"></div>
          <Card className="relative border-0 bg-card/80 backdrop-blur-sm overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 opacity-80"></div>
            <CardContent className="relative p-8 md:p-12 text-center">
              <div className="inline-flex items-center justify-center p-2 mb-4 rounded-full bg-primary/10">
                <Lock className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Enterprise-Grade Security</h3>
              <p className="text-foreground/80 mb-6 max-w-2xl mx-auto">
                TenXer is built with security at its core. Our architecture ensures your data never leaves your environment,
                with end-to-end encryption and compliance with industry standards.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90">
                  Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-primary/20 text-primary hover:bg-primary/10">
                  Schedule Demo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}